/* eslint-disable max-len */
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { shade } from 'polished';
import React, { useMemo } from 'react';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { Rect, RectLike } from '../../../platform/geom/Rect';
import { useResolve } from '../../Container';
import { Camera } from '../Camera';
import { EditorOptions } from '../EditorOptions';
import { GridSystem } from '../GridSystem';

const styling: any = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
};

const fadeInAndOut: Variants = {
    before: {
        opacity: 0,
    },
    in: {
        opacity: 1,
        transition: {
            duration: .2,
            ease: 'linear',
        }
    },
    out: {
        opacity: 0,
        transition: {
            duration: .2,
            ease: 'linear',
        }
    }
};

const SmallPattern = ({ color, left, top, width, height }: RectLike & { color: string }) =>
    <pattern id="sm" x={left} y={top} width={width} height={height} patternUnits="userSpaceOnUse" >
        <g transform={`translate(${width / 2}, ${height / 2})`}>
            <circle cx={.5} cy={.5} r="1" fill={color} />
        </g>
    </pattern>;

const LargePattern = ({ color, left, top, width, height }: RectLike & { color: string }) =>
    <pattern id="lg" x={left} y={top} width={width} height={height} patternUnits="userSpaceOnUse" >
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} stroke={color} strokeWidth="1" />
        <line x1={width / 2} y1={0} x2={width / 2} y2={height} stroke={color} strokeWidth="1" />
    </pattern>;

export const RenderGrid = () => {
    const camera = useObserverReturningStore(useResolve(Camera));
    const grid = useObserverReturningStore(useResolve(GridSystem));
    const { ui } = useResolve(EditorOptions);
    const show = ui && grid.enabled && grid.mode !== 'off' && camera.scale >= .75;

    const mesh = grid.size;
    const mr = Rect.fromPoints(camera.toView(new Rect(mesh / -2, mesh / -2, mesh, mesh)));

    const lines = grid.lines;
    const lr = Rect.fromPoints(camera.toView(new Rect(lines / -2, lines / -2, lines, lines)));

    const bg = '#F6FAFD';
    const fg = useMemo(() => shade(0.1 + (0.1 * (camera.scale / 4)), bg), [bg, camera.scale]);
    const fgs = useMemo(() => blend((Math.min(Math.max(1 - camera.scale, 0), 1) * .66), fg, bg), [fg, camera.scale]);

    return <AnimatePresence>
        {show &&
            <motion.svg style={{ ...styling }} variants={fadeInAndOut} initial="before" animate="in" exit="out">
                <defs>
                    <SmallPattern color={fg} {...mr} />
                    <LargePattern color={fgs} {...lr} />
                </defs>
                <g transform="translate(0, 0)">
                    <rect width="100%" height="100%" fill={bg} />
                    <rect width="100%" height="100%" fill="url(#sm)" />
                    {grid.mode === 'dots-lines' && <rect width="100%" height="100%" fill="url(#lg)" />}
                </g>
            </motion.svg>
        }
    </AnimatePresence>;
};

const blend = (p: number, c0: string, c1?: string): string => {
    const n = p < 0 ? p * -1 : p; const u = Math.round; const w = parseInt;
    if (c0.length > 7) {
        const f = c0.split(','); const t = (c1 ? c1 : p < 0 ? 'rgb(0,0,0)' : 'rgb(255,255,255)').split(','); const R = w(f[0].slice(4)); const G = w(f[1]); const B = w(f[2]);
        return `rgb(${(u((w(t[0].slice(4)) - R) * n) + R)},${(u((w(t[1]) - G) * n) + G)},${(u((w(t[2]) - B) * n) + B)})`;
    }
    else {
        const f = w(c0.slice(1), 16); const t = w((c1 ? c1 : p < 0 ? '#000000' : '#FFFFFF').slice(1), 16); const R1 = f >> 16; const G1 = f >> 8 & 0x00FF; const B1 = f & 0x0000FF;
        return `#${(0x1000000 + (u(((t >> 16) - R1) * n) + R1) * 0x10000 + (u(((t >> 8 & 0x00FF) - G1) * n) + G1) * 0x100 + (u(((t & 0x0000FF) - B1) * n) + B1)).toString(16).slice(1)}`;
    }
};
